/**
 * /venues/:venueSlug/admin/tournaments — create and list a venue's nights.
 *
 * Format resolution happens server-side in resolveFormat (functions/lib/
 * tournaments.js): body wins, then the venue's default_* settings, then a hard
 * default. Blank fields here are therefore sent as null rather than as 0 or '',
 * so an untouched field falls through to the venue's own default instead of
 * overriding it with an empty value.
 *
 * groupBestOfLegs is the exception — it is NOT NULL with no hard fallback, so
 * the endpoint 400s when neither the body nor a venue default supplies it. It
 * is marked required here.
 *
 * THERE IS NO GROUP COUNT, QUALIFIERS OR WILDCARDS FIELD. All three depend on
 * how many players actually check in, so they are settled at draw time in the
 * check-in modal rather than guessed at weeks earlier. Sending any of them here
 * is now a 400 — see RETIRED_FORMAT_FIELDS in lib/tournament-format.js.
 *
 * The list is intentionally read-only. Draw, run and complete are separate
 * screens against their own endpoints; this page is create-and-see.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiGet, apiPost } from '../lib/api.js';
import { useVenue } from '../context/AuthContext.jsx';
import { formatDateShort } from '../lib/format.js';
import StatusBadge from '../components/StatusBadge.jsx';
import LegCountFields, {
  KNOCKOUT_ROUNDS, blankLegCounts,
} from '../components/tournament/LegCountFields.jsx';

const blankForm = () => ({
  seasonId:           '',
  nightNumber:        '',
  date:               '',
  maxEntries:         28,
  entryFee:           0,
  ...blankLegCounts(),
  autoTournamentCompletion: false,
  rulesText:          '',
});

/** '' -> null, so an untouched field defers to the venue default (see header). */
const orNull = (value) => (value === '' || value === null ? null : value);

/* ------------------------------------------------------------------------- */

function CreateTournamentForm({ venueId, seasons, onCreated, onCancel }) {
  const [form, setForm] = useState(blankForm());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setChecked = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.checked }));

  const onSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const { data, error: err } = await apiPost(`/api/venues/${venueId}/tournaments`, {
      seasonId:           orNull(form.seasonId),
      nightNumber:        orNull(form.nightNumber),
      date:               orNull(form.date),
      maxEntries:         orNull(form.maxEntries),
      entryFee:           orNull(form.entryFee),

      groupBestOfLegs:    orNull(form.groupBestOfLegs),
      ...Object.fromEntries(
        KNOCKOUT_ROUNDS.map(({ field }) => [field, orNull(form[field])])
      ),

      autoTournamentCompletion: form.autoTournamentCompletion,
      rulesText:                form.rulesText.trim() || null,
    });

    setBusy(false);

    if (err) { setError(err); return; }
    onCreated(data);
  };

  // Only an active season can take a tournament — the endpoint 400s on any
  // other status, so the closed ones are not offered.
  const openSeasons = seasons.filter((s) => s.status === 'active');

  return (
    <form className="hd-card hd-admin-form hd-stack" onSubmit={onSubmit}>
      <h2 className="hd-section-title">Create tournament</h2>

      <div className="hd-fields">
        <div className="hd-field">
          <label className="hd-label" htmlFor="tourn-season">Season</label>
          <select
            id="tourn-season"
            className="hd-select"
            value={form.seasonId}
            onChange={set('seasonId')}
          >
            <option value="">Standalone — no season</option>
            {openSeasons.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <span className="hd-hint">Only an active season can take a night.</span>
        </div>

        <div className="hd-field">
          <label className="hd-label" htmlFor="tourn-night">Night number</label>
          <input
            id="tourn-night"
            className="hd-input"
            type="number"
            min="1"
            value={form.nightNumber}
            onChange={set('nightNumber')}
          />
        </div>

        <div className="hd-field">
          <label className="hd-label" htmlFor="tourn-date">Date</label>
          <input
            id="tourn-date"
            className="hd-input"
            type="date"
            value={form.date}
            onChange={set('date')}
          />
        </div>
      </div>

      <h3 className="hd-section-title">Entries</h3>

      <div className="hd-fields">
        <div className="hd-field">
          <label className="hd-label" htmlFor="tourn-max">Max entries</label>
          <input
            id="tourn-max"
            className="hd-input"
            type="number"
            min="1"
            value={form.maxEntries}
            onChange={set('maxEntries')}
          />
        </div>

        <div className="hd-field">
          <label className="hd-label" htmlFor="tourn-fee">Entry fee</label>
          <input
            id="tourn-fee"
            className="hd-input"
            type="number"
            min="0"
            step="0.01"
            value={form.entryFee}
            onChange={set('entryFee')}
          />
        </div>
      </div>

      <h3 className="hd-section-title">Format</h3>

      <p className="hd-hint">
        Groups and knockout size are chosen on the night, when you start the
        tournament and know who turned up.
      </p>

      <LegCountFields form={form} set={set} />

      <h3 className="hd-section-title">Automation</h3>

      <label className="hd-row" style={{ gap: 8, fontSize: 13 }}>
        <input
          type="checkbox"
          checked={form.autoTournamentCompletion}
          onChange={setChecked('autoTournamentCompletion')}
        />
        Complete the tournament automatically when the final is played
      </label>

      <div className="hd-field">
        <label className="hd-label" htmlFor="tourn-rules">Rules text</label>
        <textarea
          id="tourn-rules"
          className="hd-textarea"
          value={form.rulesText}
          onChange={set('rulesText')}
          placeholder="Anything players should read before entering."
        />
        <span className="hd-hint">Optional.</span>
      </div>

      {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

      <div className="hd-row">
        <button className="hd-btn hd-btn-primary" type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create tournament'}
        </button>
        <button className="hd-btn hd-btn-quiet" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------------- */

export default function AdminTournaments() {
  const { venueSlug } = useParams();
  const contextVenue = useVenue(venueSlug);
  const contextId = contextVenue?.venue_id ?? null;

  const [state, setState] = useState({ status: 'loading' });
  const [venueId, setVenueId] = useState(contextId);
  const [seasons, setSeasons] = useState([]);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState(null);

  const loadTournaments = useCallback(async (id) => {
    const { data, error: err } = await apiGet(`/api/venues/${id}/tournaments`);
    if (err) return { status: 'error', error: err };
    return { status: 'ready', tournaments: data?.tournaments ?? [] };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    (async () => {
      let id = contextId;

      // Platform staff hold no user_venue_roles row, so the auth context has no
      // entry for a venue they can still administer.
      if (id === null) {
        const { data, error: err } = await apiGet(`/api/venues/by-slug/${venueSlug}`);
        if (cancelled) return;

        if (err || !data?.venue) {
          setState({ status: 'error', error: err ?? 'Venue not found' });
          return;
        }
        id = data.venue.id;
      }

      if (cancelled) return;
      setVenueId(id);

      // The season list only fills a dropdown — a failure here must not stop
      // the page, because a standalone tournament needs no season at all.
      const [next, seasonsRes] = await Promise.all([
        loadTournaments(id),
        apiGet(`/api/venues/${id}/seasons`),
      ]);

      if (cancelled) return;
      setSeasons(seasonsRes.data?.seasons ?? []);
      setState(next);
    })();

    return () => { cancelled = true; };
  }, [venueSlug, contextId, loadTournaments]);

  if (state.status === 'loading') {
    return <div className="hd-empty">Loading…</div>;
  }

  if (state.status === 'error') {
    return (
      <div className="hd-stack">
        <h1 className="hd-page-title">Tournaments</h1>
        <p className="hd-muted">{state.error}</p>
      </div>
    );
  }

  const { tournaments } = state;

  return (
    <div className="hd-stack">
      <div className="hd-row-between">
        <div>
          <h1 className="hd-page-title">Tournaments</h1>
          <p className="hd-meta">{contextVenue?.name ?? venueSlug}</p>
        </div>

        <button
          className="hd-btn hd-btn-primary"
          type="button"
          onClick={() => { setCreating((open) => !open); setNotice(null); }}
        >
          Create tournament
        </button>
      </div>

      {notice && <div className="hd-badge hd-badge-success" role="status">{notice}</div>}

      {creating && (
        <CreateTournamentForm
          venueId={venueId}
          seasons={seasons}
          onCancel={() => setCreating(false)}
          onCreated={async (data) => {
            setCreating(false);
            const night = data?.tournament?.night_number;
            setNotice(night != null ? `Night ${night} created.` : 'Tournament created.');
            setState(await loadTournaments(venueId));
          }}
        />
      )}

      {tournaments.length === 0 ? (
        <div className="hd-empty">
          No tournaments yet. Create one to open registration.
        </div>
      ) : (
        <div className="hd-table-wrap">
          {/* The list endpoint returns raw tournament rows (SELECT t.*), so
              these are snake_case — unlike the seasons endpoint, which maps to
              camelCase before returning. */}
          <table className="hd-table hd-admin-table">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Night</th>
                <th>Date</th>
                <th>Season</th>
                <th>Status</th>
                <th className="hd-table-num">Entries</th>
                <th className="hd-table-num">Group</th>
                <th className="hd-table-num">Final</th>
              </tr>
            </thead>
            <tbody>
              {tournaments.map((t) => (
                <tr key={t.id}>
                  <td>
                    <Link
                      className="hd-link-button"
                      to={`/venues/${venueSlug}/admin/tournaments/${t.id}`}
                    >
                      {t.night_number != null ? `Night ${t.night_number}` : `#${t.id}`}
                    </Link>
                  </td>
                  <td>{formatDateShort(t.date) ?? 'Date TBC'}</td>
                  <td>{t.season_name ?? <span className="hd-muted">Standalone</span>}</td>
                  <td><StatusBadge status={t.status} /></td>
                  <td className="hd-table-num">{t.entry_count} / {t.max_entries}</td>
                  <td className="hd-table-num">{t.group_best_of_legs}</td>
                  <td className="hd-table-num">{t.final_best_of_legs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
